import { useContext, useEffect, useMemo, useState } from 'react';
import { NodeResizer, NodeToolbar, Position, useReactFlow } from '@xyflow/react';
import { Trans } from '@lingui/react/macro';
import get from 'lodash/get';
import { HexColorPicker } from 'react-colorful';

import WorkflowContext from '../../Workflow/WorkflowContext';
import DefaultHandles from './handles/DefaultHandles';

import getStepType from '../../Workflow/stepTypes';
import { useDarkMode } from '../../../../context/DarkModeContext';

import '@xyflow/react/dist/style.css';

function DefaultNode({ shape, icon: StepIcon, handles, id, positionAbsoluteX: x, positionAbsoluteY: y, width, height, isConnectable }) {
  const { state, dispatchAction } = useContext(WorkflowContext);
  const reactFlowInstance = useReactFlow();
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const { isDarkMode } = useDarkMode();

  const step = useMemo(() => {
    return get(state, ['steps', id]);
  }, [state, id]);

  const role = useMemo(() => {
    return get(step, 'role');
  }, [step]);

  const selected = useMemo(() => {
    return state.selection === id;
  }, [state, id]);

  const stepColor = useMemo(() => {
    return step?.color || '#000000';
  }, [step]);

  const borderColor = useMemo(() => {
    if (!role) return 'lightgray';
    if (selected) return 'rgb(19, 117, 237)';

    return stepColor;
  }, [selected, role]);

  const containerStyle = useMemo(
    () => ({
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      width,
      height,
      borderStyle: 'solid', // isTarget ? 'dashed' : 'solid',
      backgroundColor: isDarkMode ? '#2a2d33' : 'white',
      borderWidth: '1px',
      cursor: 'pointer',
      borderRadius: shape === 'oval' ? '50%' : '10px',
      borderColor: borderColor,
      ...(shape === 'fork' || shape === 'join'
        ? {
            borderWidth: '2px',
            borderStyle: 'dashed',
            backgroundColor: isDarkMode ? '#32363d' : '#f8f9fa',
          }
        : {}),
      boxShadow: selected
        ? 'rgba(19, 117, 237, 0.16) 0px 1px 2px, rgb(19, 117, 237) 0px 0px 0px 2px, rgba(19, 117, 237, 0.08) 0px 3px 6px, rgba(19, 117, 237, 0.5) 0px 0px 0px 6px'
        : 'none',
    }),
    [selected, shape, width, height, role, isDarkMode]
  );

  useEffect(() => {
    if (!selected) {
      setColorPickerOpen(false);
    }
  }, [selected]);

  return (
    <div className="basic-node" style={containerStyle}>
      <NodeResizer
        color="rgb(19, 117, 237)"
        isVisible={selected}
        minWidth={90}
        minHeight={40}
        onResizeEnd={(e, params) => dispatchAction.setStepSize({ name: id, size: { width: params.width, height: params.height } })}
      />
      <NodeToolbar isVisible={selected} position={Position.Top}>
        {colorPickerOpen && (
          <HexColorPicker
            className="nodrag nopan"
            color={stepColor}
            onChange={newColor => {
              dispatchAction.setStepColor({ name: id, color: newColor });
            }}
          />
        )}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '0.7rem' }}>
          <button className="xy-theme__button" onClick={() => dispatchAction.duplicateStep({ name: id, reactFlowInstance })}>
            <Trans>duplicate</Trans>
          </button>
          <button className="xy-theme__button" onClick={() => dispatchAction.removeStep({ name: id, reactFlowInstance })}>
            <Trans>delete</Trans>
          </button>
          <button
            className="xy-theme__button"
            style={{ backgroundColor: stepColor, width: '1rem', height: '1rem', borderRadius: '100%' }}
            onClick={() => setColorPickerOpen(!colorPickerOpen)}
          />
        </div>
      </NodeToolbar>
      {handles ? handles : <DefaultHandles id={id} width={width} height={height} isConnectable={isConnectable} />}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          textWrap: 'balance',
          color: role ? (isDarkMode ? '#e2e4e9' : 'black') : borderColor,
          width,
          height,
        }}
      >
        {step?.title}
      </div>
      {StepIcon && (
        <div
          style={{
            position: 'absolute',
            top: -15,
            left: -12,
            backgroundColor: isDarkMode ? '#2a2d33' : 'white',
            borderRadius: '50%',
            padding: '4px',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: borderColor,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <StepIcon size={18} style={{ color: borderColor }} />
        </div>
      )}
    </div>
  );
}

export default DefaultNode;
